import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';

import { DataGrid, GridColDef, GridSortModel, GridRenderEditCellParams, GridPreProcessEditCellProps } from '@mui/x-data-grid';

import Badge from '@mui/material/Badge';

import { format, parse } from 'date-fns';
import { logEvent } from '../../services/analytics';
import { frostedDrawerPaperSx, frostedDrawerHeaderSx } from '../../theme/componentStyles';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
interface AttributeTableDrawerProps {
  open: boolean;
  onClose: () => void;
  layers: any[];
  selectedLayerId: number;
  onLayerChange: (layerId: number) => void;
  onSelectShapes?: (selectedIds: (string | number)[]) => void;
  onUpdateShape?: (layerId: number, shapeId: string, patch: Record<string, any>) => boolean | void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  editable?: boolean;
}

const buildShapeColumns = (t: (k: string) => string): GridColDef[] => [
  { field: 'id', headerName: t('attributeTable.columns.id'), width: 120, sortable: true },
  { field: 'type', headerName: t('attributeTable.columns.type'), width: 90, sortable: true },
  { field: 'name', headerName: t('attributeTable.columns.name'), width: 160, sortable: true },
  { field: 'description', headerName: t('attributeTable.columns.description'), width: 180, sortable: true },
  { field: 'coveredPopulation', headerName: t('attributeTable.columns.coveredPopulation'), width: 160, sortable: true },
  { field: 'uncoveredPopulation', headerName: t('attributeTable.columns.uncoveredPopulation'), width: 180, sortable: true },
  { field: 'population', headerName: t('attributeTable.columns.population'), width: 140, sortable: true },
  { field: 'latitude', headerName: t('attributeTable.columns.latitude'), width: 120, sortable: true },
  { field: 'longitude', headerName: t('attributeTable.columns.longitude'), width: 120, sortable: true },
];

// Helper for robust date parsing
const parseDateRobust = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  // Try ISO and common formats
  const formats = [
    "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
    "yyyy-MM-dd'T'HH:mm:ss.SSSX",
    "yyyy-MM-dd'T'HH:mm:ssX",
    "yyyy-MM-dd'T'HH:mm:ss",
    'yyyy-MM-dd HH:mm:ss',
    'yyyy-MM-dd HH:mm',
    'yyyy-MM-dd',
    'dd/MM/yyyy HH:mm:ss',
    'dd/MM/yyyy HH:mm',
    'dd/MM/yyyy',
    'MM/dd/yyyy HH:mm:ss',
    'MM/dd/yyyy HH:mm',
    'MM/dd/yyyy',
    'dd MMM yyyy, HH:mm',
    'dd MMM yyyy HH:mm',
    'dd MMM yyyy',
    'MMM dd, yyyy HH:mm',
    'MMM dd, yyyy',
    'yyyy/MM/dd HH:mm:ss',
    'yyyy/MM/dd HH:mm',
    'yyyy/MM/dd',
    'dd-MM-yyyy HH:mm:ss',
    'dd-MM-yyyy HH:mm',
    'dd-MM-yyyy',
    'MM-dd-yyyy HH:mm:ss',
    'MM-dd-yyyy HH:mm',
    'MM-dd-yyyy',
  ];
  for (const fmt of formats) {
    const parsed = parse(value, fmt, new Date());
    if (!isNaN(parsed.getTime())) return parsed;
  }
  // Try Date constructor as fallback
  const d = new Date(value);
  if (!isNaN(d.getTime())) return d;
  return null;
};

const toDateTimeLocalValue = (value: any): string => {
  const parsed = parseDateRobust(value);
  if (!parsed) return '';
  return format(parsed, "yyyy-MM-dd'T'HH:mm");
};

const toDisplayTimeOnly = (value: any): string => {
  if (!value) return '';
  const parsed = parseDateRobust(value);
  if (parsed) return format(parsed, 'HH:mm');
  if (typeof value === 'string') {
    const hhmm = value.match(/(\d{2}:\d{2})/);
    if (hhmm?.[1]) return hhmm[1];
  }
  return '';
};

const RequestTimeEditCell: React.FC<GridRenderEditCellParams> = (params) => {
  const { t } = useTranslation('mapDialogs');
  const changeDateTimeLabel = t('attributeTable.changeDateTime');
  const inputId = `request-time-picker-${String(params.id)}`;
  const initialParsed = parseDateRobust(params.value) || new Date();
  const [dateTimeValue, setDateTimeValue] = useState<string>(toDateTimeLocalValue(initialParsed));
  const [timeValue, setTimeValue] = useState<string>(format(initialParsed, 'HH:mm'));

  useEffect(() => {
    const parsed = parseDateRobust(params.value);
    if (!parsed) return;
    setDateTimeValue(toDateTimeLocalValue(parsed));
    setTimeValue(format(parsed, 'HH:mm'));
  }, [params.value]);

  const openPicker = () => {
    const el = document.getElementById(inputId) as HTMLInputElement | null;
    if (!el) return;
    if (typeof (el as any).showPicker === 'function') {
      (el as any).showPicker();
    } else {
      el.focus();
      el.click();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, pl: 0.5 }}>
      <TextField
        autoFocus
        type="time"
        size="small"
        value={timeValue}
        onChange={async (event) => {
          const nextTime = event.target.value;
          setTimeValue(nextTime);
          if (!nextTime || !/^\d{2}:\d{2}$/.test(nextTime)) {
            return;
          }

          const base = parseDateRobust(dateTimeValue) || parseDateRobust(params.value) || new Date();
          const [hours, minutes] = nextTime.split(':').map((v) => Number(v));
          if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
            return;
          }
          base.setHours(hours, minutes, 0, 0);
          const nextDateTime = toDateTimeLocalValue(base);
          setDateTimeValue(nextDateTime);

          await params.api.setEditCellValue(
            {
              id: params.id,
              field: params.field,
              value: nextDateTime,
            },
            event
          );
        }}
        onBlur={() => {
          params.api.stopCellEditMode({ id: params.id, field: params.field });
        }}
        inputProps={{ step: 60 }}
        sx={{
          '& .MuiInputBase-input': {
            fontSize: '0.78rem',
            py: 0.4,
            px: 0.5,
          },
          flexGrow: 1,
          mr: 0.25,
        }}
      />
      <IconButton size="small" onClick={openPicker} title={changeDateTimeLabel} sx={{ p: 0.25 }}>
        <Icon size={16} name="calendar" />
      </IconButton>
      <input
        id={inputId}
        type="datetime-local"
        value={dateTimeValue}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        onChange={async (event) => {
          const nextValue = event.target.value;
          setDateTimeValue(nextValue);
          const parsed = parseDateRobust(nextValue);
          if (parsed) {
            setTimeValue(format(parsed, 'HH:mm'));
          }
          await params.api.setEditCellValue(
            {
              id: params.id,
              field: params.field,
              value: nextValue,
            },
            event as any
          );
          params.api.stopCellEditMode({ id: params.id, field: params.field });
        }}
      />
    </Box>
  );
};

const buildTripColumns = (t: (k: string) => string): GridColDef[] => [
  { field: 'ride_id', headerName: t('attributeTable.columns.rideId'), width: 100 },
  { field: 'user_id', headerName: t('attributeTable.columns.userId'), width: 100 },
  {
    field: 'from_address',
    headerName: t('attributeTable.columns.pickupAddress'),
    width: 220,
    renderCell: (params) => {
      const row = (params as any).row;
      return row.from_address ?? row.pickup_address ?? '';
    },
  },
  {
    field: 'to_address',
    headerName: t('attributeTable.columns.dropoffAddress'),
    width: 220,
    renderCell: (params) => {
      const row = (params as any).row;
      return row.to_address ?? row.dropoff_address ?? '';
    },
  },
  {
    field: 'origin_lng',
    headerName: t('attributeTable.columns.originLng'),
    width: 120,
    renderCell: (params) => {
      const row = (params as any).row;
      return row.origin_lng ?? row.coordinates?.[0]?.[1] ?? '';
    },
  },
  {
    field: 'origin_lat',
    headerName: t('attributeTable.columns.originLat'),
    width: 120,
    renderCell: (params) => {
      const row = (params as any).row;
      return row.origin_lat ?? row.coordinates?.[0]?.[0] ?? '';
    },
  },
  {
    field: 'dest_lng',
    headerName: t('attributeTable.columns.destLng'),
    width: 120,
    renderCell: (params) => {
      const row = (params as any).row;
      return row.dest_lng ?? row.coordinates?.[1]?.[1] ?? '';
    },
  },
  {
    field: 'dest_lat',
    headerName: t('attributeTable.columns.destLat'),
    width: 120,
    renderCell: (params) => {
      const row = (params as any).row;
      return row.dest_lat ?? row.coordinates?.[1]?.[0] ?? '';
    },
  },
  {
    field: 'request_time',
    headerName: t('attributeTable.columns.requestTime'),
    width: 170,
    renderEditCell: (params: GridRenderEditCellParams) => <RequestTimeEditCell {...params} />,
    renderCell: (params) => {
      const row = (params as any).row;
      const value = row?.request_time ?? row?.time;
      if (!value) return '';
      try {
        return toDisplayTimeOnly(value);
      } catch {}
      return '';
    },
  },
  {
    field: 'standard_seat',
    headerName: t('attributeTable.columns.standardSeat'),
    width: 120,
    renderCell: (params) => {
      const row = (params as any).row;
      return row.standard_seat ?? row.numPassengers ?? '';
    },
  },
  { field: 'wheelchair', headerName: t('attributeTable.columns.wheelchair'), width: 100 },
  {
    field: 'request_type',
    headerName: t('attributeTable.columns.requestType'),
    width: 170,
    type: 'singleSelect',
    valueOptions: ['arrival_fixed', 'departure_fixed'],
    renderEditCell: (params: GridRenderEditCellParams) => {
      const currentValue = (params.value ?? '') as string;
      return (
        <Select
          autoFocus
          defaultOpen
          value={currentValue}
          size="small"
          fullWidth
          onChange={async (event) => {
            const nextValue = event.target.value as string;
            await params.api.setEditCellValue(
              {
                id: params.id,
                field: params.field,
                value: nextValue,
              },
              event
            );
            params.api.stopCellEditMode({ id: params.id, field: params.field });
          }}
          MenuProps={{
            sx: { zIndex: 2400 },
            PaperProps: {
              sx: { zIndex: 2401 },
            },
          }}
        >
          <MenuItem value="arrival_fixed">arrival_fixed</MenuItem>
          <MenuItem value="departure_fixed">departure_fixed</MenuItem>
        </Select>
      );
    },
  },
];

const getRowIdFromShape = (shape: any, index: number, isTripLayer: boolean): string | number => {
  if (shape.id !== undefined && shape.id !== null && shape.id !== '') return shape.id;
  if (isTripLayer && shape.ride_id) return String(shape.ride_id);
  if (shape.fid !== undefined && shape.fid !== null && shape.fid !== '') return shape.fid;
  return `${isTripLayer ? 'trip' : 'shape'}-${index}`;
};

const normalizeEditValue = (field: string, value: any, t: (k: string, p?: any) => any): any => {
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (field === 'standard_seat' || field === 'wheelchair') {
      if (trimmed === '') return null;
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        throw new Error(t('attributeTable.errorMustBeNonNegative', { field }));
      }
      return Math.round(parsed);
    }

    if (field === 'request_time') {
      if (trimmed === '') return '';
      const parsed = parseDateRobust(trimmed);
      if (!parsed) {
        throw new Error(t('attributeTable.errorInvalidDateTime'));
      }
      return trimmed;
    }

    if (field === 'request_type') {
      if (trimmed !== 'arrival_fixed' && trimmed !== 'departure_fixed') {
        throw new Error(t('attributeTable.errorInvalidRequestType'));
      }
      return trimmed;
    }

    if (field === 'ride_id' || field === 'user_id') {
      return trimmed;
    }

    if (field === 'name' || field === 'description') {
      return trimmed;
    }
  }

  return value;
};

const AttributeTableDrawer: React.FC<AttributeTableDrawerProps> = ({
  open,
  onClose,
  layers,
  selectedLayerId,
  onLayerChange,
  onSelectShapes,
  onUpdateShape,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  editable = false,
}) => {
  const { t } = useTranslation('mapDialogs');
  const SHAPE_COLUMNS = useMemo(() => buildShapeColumns(t), [t]);
  const TRIP_COLUMNS = useMemo(() => buildTripColumns(t), [t]);
  const selectedLayer = useMemo(
    () => layers.find((l) => l.layerId === selectedLayerId),
    [layers, selectedLayerId]
  );
  const shapes = useMemo(() => (selectedLayer ? selectedLayer.shapes : []), [selectedLayer]);

  // Track selected row IDs
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // Determine if this is a trip layer (by checking for ride_id in shapes)
  const isTripLayer = shapes.length > 0 &&
    shapes.some(
      (shape: any) =>
        shape.ride_id !== undefined ||
        (shape.type === 'line' && shape.originPointId && shape.destPointId)
    );

  // Determine if any polygon has population analysis fields
  const hasPopulationAnalysis = shapes.some(
    (shape: any) =>
      shape.type === 'polygon' &&
      (typeof shape.totalPopulation === 'number' ||
        typeof shape.coveredPopulation === 'number' ||
        typeof shape.desertPopulation === 'number' ||
        typeof shape.percentCovered === 'number' ||
        typeof shape.percentDesert === 'number')
  );

  // Track drawer opening
  useEffect(() => {
    if (open && selectedLayer) {
      logEvent({
        category: 'Attribute Table',
        action: 'AttributeTableOpened',
        label: selectedLayer.layerName || 'Unnamed Layer',
        layer_id: selectedLayerId,
        layer_shape_count: shapes.length,
        layer_shape_types: Array.from(new Set(shapes.map((s: any) => s.type))).join(','),
        is_trip_layer: isTripLayer,
        has_population_analysis: hasPopulationAnalysis,
      });
    }
  }, [open, selectedLayer, selectedLayerId, shapes, isTripLayer, hasPopulationAnalysis]);

  // Dynamically add population columns if needed
  const dynamicShapeColumns = useMemo(() => {
    let nextColumns = [...SHAPE_COLUMNS];
    if (hasPopulationAnalysis) {
      // Remove old pop columns if present
      nextColumns = nextColumns.filter(
        (col) => !['coveredPopulation', 'uncoveredPopulation', 'population'].includes(col.field)
      );
      nextColumns.push(
        { field: 'totalPopulation', headerName: t('attributeTable.columns.totalPopulation'), width: 140, sortable: true },
        { field: 'coveredPopulation', headerName: t('attributeTable.columns.coveredByTransit'), width: 160, sortable: true },
        { field: 'desertPopulation', headerName: t('attributeTable.columns.inTransitDesert'), width: 160, sortable: true }
      );
    }
    return nextColumns;
  }, [hasPopulationAnalysis, SHAPE_COLUMNS, t]);

  // Only show lines for trip layers
  const filteredShapes = isTripLayer ? shapes.filter((shape: any) => shape.type === 'line') : shapes;

  const editableTripFields = useMemo(
    () => new Set(['ride_id', 'user_id', 'request_time', 'request_type', 'standard_seat', 'wheelchair']),
    []
  );
  const editableShapeFields = useMemo(() => new Set(['name', 'description']), []);

  const baseColumns = isTripLayer ? TRIP_COLUMNS : dynamicShapeColumns;
  const columns = useMemo(
    () =>
      baseColumns.map((col) => {
        const isEditable =
          editable &&
          Boolean(onUpdateShape) &&
          (isTripLayer ? editableTripFields.has(col.field) : editableShapeFields.has(col.field));

        if (isTripLayer && col.field === 'ride_id') {
          return {
            ...col,
            align: col.align ?? 'center',
            headerAlign: col.headerAlign ?? 'center',
            editable: isEditable,
            renderEditCell: (params: GridRenderEditCellParams) => {
              const errorText = typeof params.error === 'string' ? params.error : '';
              return (
                <TextField
                  autoFocus
                  size="small"
                  fullWidth
                  value={String(params.value ?? '')}
                  error={Boolean(errorText)}
                  helperText={errorText || ' '}
                  onChange={async (event) => {
                    await params.api.setEditCellValue(
                      {
                        id: params.id,
                        field: params.field,
                        value: event.target.value,
                      },
                      event
                    );
                  }}
                  onBlur={() => {
                    params.api.stopCellEditMode({ id: params.id, field: params.field });
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '0.78rem',
                      py: 0.5,
                      px: 0.5,
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '0.68rem',
                      lineHeight: 1.1,
                      m: 0,
                      mt: 0.2,
                      textAlign: 'left',
                    },
                  }}
                />
              );
            },
            preProcessEditCellProps: (
              params: GridPreProcessEditCellProps
            ): GridPreProcessEditCellProps['props'] => {
              const nextRideId = String(params.props.value ?? '').trim();
              if (!nextRideId) {
                return { ...params.props, error: t('attributeTable.errorRideIdRequired') };
              }

              const duplicate = filteredShapes.some((shape: any, i: number) => {
                const candidateRowId = getRowIdFromShape(shape, i, true);
                if (String(candidateRowId) === String(params.id)) return false;
                return String(shape.ride_id ?? '').trim() === nextRideId;
              });

              if (duplicate) {
                return { ...params.props, error: t('attributeTable.errorRideIdDuplicate', { value: nextRideId }) };
              }
              return { ...params.props, error: false };
            },
          };
        }

        return {
          ...col,
          align: col.align ?? 'center',
          headerAlign: col.headerAlign ?? 'center',
          editable: isEditable,
        };
      }),
    [baseColumns, editable, onUpdateShape, isTripLayer, editableTripFields, editableShapeFields, filteredShapes]
  );

  const tableRows = useMemo(
    () =>
      filteredShapes.map((shape: any, i: number) => {
        if (isTripLayer) {
          // For trip layers, use dynamic point positions if available
          let originLat = '';
          let originLng = '';
          let destLat = '';
          let destLng = '';
          if ('originPointId' in shape && 'destPointId' in shape && Array.isArray(shapes)) {
            const origin = shapes.find((s: any) => s.id === shape.originPointId && s.type === 'point');
            const dest = shapes.find((s: any) => s.id === shape.destPointId && s.type === 'point');
            if (origin) {
              originLat = origin.coordinates[0];
              originLng = origin.coordinates[1];
            }
            if (dest) {
              destLat = dest.coordinates[0];
              destLng = dest.coordinates[1];
            }
          }
          if (originLat === '' && shape.coordinates?.[0]) {
            originLat = shape.coordinates[0][0];
            originLng = shape.coordinates[0][1];
          }
          if (destLat === '' && shape.coordinates?.[1]) {
            destLat = shape.coordinates[1][0];
            destLng = shape.coordinates[1][1];
          }
          return {
            ...shape,
            id: getRowIdFromShape(shape, i, true),
            request_time: shape.request_time ?? shape.time ?? '',
            origin_lat: originLat,
            origin_lng: originLng,
            dest_lat: destLat,
            dest_lng: destLng,
          };
        }

        // Default shape row
        const row: any = { id: getRowIdFromShape(shape, i, false) };
        dynamicShapeColumns.forEach((col) => {
          if (col.field !== 'id') {
            row[col.field] = shape[col.field] !== undefined ? shape[col.field] : '';
          }
        });
        row.latitude = shape.type === 'point' && Array.isArray(shape.coordinates) ? shape.coordinates[0] : '';
        row.longitude = shape.type === 'point' && Array.isArray(shape.coordinates) ? shape.coordinates[1] : '';

        // Add population for raster shapes
        if (shape.type === 'raster' && Array.isArray(shape.data)) {
          const totalPop = shape.data.reduce((sum: number, p: any) => sum + (p.population || 0), 0);
          row.population = Math.round(totalPop); // No decimals
        } else if (shape.type === 'polygon') {
          // For polygons, sum covered and uncovered population if present
          const covered = typeof shape.coveredPopulation === 'number' ? shape.coveredPopulation : 0;
          const uncovered = typeof shape.uncoveredPopulation === 'number' ? shape.uncoveredPopulation : 0;
          row.coveredPopulation = covered ? Math.round(covered).toLocaleString() : '';
          row.uncoveredPopulation = uncovered ? Math.round(uncovered).toLocaleString() : '';
          row.population = covered + uncovered ? Math.round(covered + uncovered).toLocaleString() : '';
        } else {
          row.population = '';
        }
        return row;
      }),
    [filteredShapes, isTripLayer, shapes, dynamicShapeColumns]
  );

  const rowToShapeMap = useMemo(() => {
    const shapeMap = new Map<string | number, { shape: any; index: number }>();
    filteredShapes.forEach((shape: any, i: number) => {
      const rowId = getRowIdFromShape(shape, i, isTripLayer);
      shapeMap.set(rowId, { shape, index: i });
    });
    return shapeMap;
  }, [filteredShapes, isTripLayer]);

  const handleProcessRowUpdate = useCallback(
    (newRow: any, oldRow: any) => {
      if (!editable || !onUpdateShape) return oldRow;

      const mapped = rowToShapeMap.get(newRow.id);
      if (!mapped?.shape?.id) {
        throw new Error(t('attributeTable.errorUnableToUpdate'));
      }

      const changedKeys = Object.keys(newRow).filter(
        (key) =>
          !['id', 'origin_lat', 'origin_lng', 'dest_lat', 'dest_lng', 'latitude', 'longitude'].includes(key) &&
          newRow[key] !== oldRow[key]
      );
      if (changedKeys.length === 0) return oldRow;

      const patch: Record<string, any> = {};
      for (const key of changedKeys) {
        const allowed = isTripLayer ? editableTripFields.has(key) : editableShapeFields.has(key);
        if (!allowed) {
          throw new Error(t('attributeTable.errorFieldReadOnly', { field: key }));
        }
        patch[key] = normalizeEditValue(key, newRow[key], t);
      }

      // Prevent duplicate ride IDs across trip rows.
      if (isTripLayer && Object.prototype.hasOwnProperty.call(patch, 'ride_id')) {
        const nextRideId = String(patch.ride_id ?? '').trim();
        if (nextRideId !== '') {
          const hasDuplicate = filteredShapes.some((shape: any) => {
            const isSameShape = String(shape.id) === String(mapped.shape.id);
            if (isSameShape) return false;
            const existingRideId = String(shape.ride_id ?? '').trim();
            return existingRideId !== '' && existingRideId === nextRideId;
          });
          if (hasDuplicate) {
            throw new Error(t('attributeTable.errorRideIdDuplicateField', { value: nextRideId }));
          }
        }
      }

      const updateResult = onUpdateShape(selectedLayerId, String(mapped.shape.id), patch);
      if (updateResult === false) {
        throw new Error(t('attributeTable.errorUpdateRejected'));
      }

      return { ...newRow, ...patch };
    },
    [
      editable,
      onUpdateShape,
      rowToShapeMap,
      isTripLayer,
      editableTripFields,
      editableShapeFields,
      filteredShapes,
      selectedLayerId,
      t,
    ]
  );

  // CSV download handler
  const handleDownloadCSV = () => {
    // Only export lines for trip layers, matching the DataGrid
    let exportShapes = isTripLayer ? shapes.filter((shape: any) => shape.type === 'line') : shapes;

    if (selectedIds.length > 0) {
      exportShapes = exportShapes.filter((shape: any, i: number) => {
        const id = getRowIdFromShape(shape, i, isTripLayer);
        return selectedIds.includes(id);
      });
    }

    if (!exportShapes || exportShapes.length === 0) return;

    // Track CSV export
    logEvent({
      category: 'Attribute Table',
      action: 'CSVExport',
      label: selectedLayer?.layerName || 'Unnamed Layer',
      layer_id: selectedLayerId,
      total_shapes: shapes.length,
      exported_shapes: exportShapes.length,
      selected_shapes: selectedIds.length,
      is_trip_layer: isTripLayer,
      has_population_analysis: hasPopulationAnalysis,
    });

    const csvRows = [columns.map((col) => col.field).join(',')];
    exportShapes.forEach((shape: any, i: number) => {
      let row: any = { id: getRowIdFromShape(shape, i, isTripLayer) };
      if (isTripLayer) {
        // For trip layers, use dynamic point positions if available (match DataGrid)
        let originLat = '';
        let originLng = '';
        let destLat = '';
        let destLng = '';
        if ('originPointId' in shape && 'destPointId' in shape && Array.isArray(shapes)) {
          const origin = shapes.find((s: any) => s.id === shape.originPointId && s.type === 'point');
          const dest = shapes.find((s: any) => s.id === shape.destPointId && s.type === 'point');
          if (origin) {
            originLat = origin.coordinates[0];
            originLng = origin.coordinates[1];
          }
          if (dest) {
            destLat = dest.coordinates[0];
            destLng = dest.coordinates[1];
          }
        }
        if (originLat === '' && shape.coordinates?.[0]) {
          originLat = shape.coordinates[0][0];
          originLng = shape.coordinates[0][1];
        }
        if (destLat === '' && shape.coordinates?.[1]) {
          destLat = shape.coordinates[1][0];
          destLng = shape.coordinates[1][1];
        }
        // Format request_time as in the table
        const requestTimeRaw = shape.request_time || shape.time || '';
        let requestTimeFormatted = '';
        if (requestTimeRaw) {
          try {
            const d = new Date(requestTimeRaw);
            if (!isNaN(d.getTime())) {
              requestTimeFormatted = format(d, 'dd MMM yyyy, HH:mm');
            } else {
              requestTimeFormatted = requestTimeRaw;
            }
          } catch {
            requestTimeFormatted = requestTimeRaw;
          }
        }
        // Create a separate row object for CSV export, don't modify original shape
        const csvRow = {
          ...shape,
          id: getRowIdFromShape(shape, i, true),
          origin_lat: originLat,
          origin_lng: originLng,
          dest_lat: destLat,
          dest_lng: destLng,
          request_time: requestTimeFormatted,
        };
        row = csvRow;
      } else {
        columns.forEach((col) => {
          if (col.field !== 'id') {
            row[col.field] = shape[col.field] !== undefined ? shape[col.field] : '';
          }
        });
        row.latitude = shape.type === 'point' && Array.isArray(shape.coordinates) ? shape.coordinates[0] : '';
        row.longitude = shape.type === 'point' && Array.isArray(shape.coordinates) ? shape.coordinates[1] : '';
      }
      const values = columns.map((col) => `"${(row[col.field] ?? '').toString().replace(/"/g, '""')}"`);
      csvRows.push(values.join(','));
    });
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (selectedLayer?.layerName || 'attribute-table') + '.csv';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  // KML download handler
  const handleDownloadKML = () => {
    let exportShapes = shapes;
    if (selectedIds.length > 0) {
      exportShapes = shapes.filter((shape: any, i: number) => {
        const id = getRowIdFromShape(shape, i, false);
        return selectedIds.includes(id);
      });
    }
    if (!exportShapes || exportShapes.length === 0) return;

    // Track KML export
    logEvent({
      category: 'Attribute Table',
      action: 'KMLExport',
      label: selectedLayer?.layerName || 'Unnamed Layer',
      layer_id: selectedLayerId,
      total_shapes: shapes.length,
      exported_shapes: exportShapes.length,
      selected_shapes: selectedIds.length,
      is_trip_layer: isTripLayer,
      has_population_analysis: hasPopulationAnalysis,
    });

    // Helper to escape XML
    const esc = (str: string) =>
      (str || '').replace(/[<>&'"]/g, (c) =>
        c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '&' ? '&amp;' : c === '"' ? '&quot;' : c === "'" ? '&apos;' : c
      );
    let kml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    kml += `<kml xmlns="http://www.opengis.net/kml/2.2"><Document>\n`;
    for (const shape of exportShapes) {
      if (shape.type === 'point') {
        kml += `<Placemark><name>${esc(shape.name || 'Point')}</name><Point><coordinates>${shape.coordinates[1]},${shape.coordinates[0]},0</coordinates></Point></Placemark>\n`;
      } else if (shape.type === 'line') {
        kml += `<Placemark><name>${esc(shape.name || 'Line')}</name><LineString><coordinates>${shape.coordinates.map(([lat, lon]: [number, number]) => `${lon},${lat},0`).join(' ')}</coordinates></LineString></Placemark>\n`;
      } else if (shape.type === 'polygon') {
        // Only supports first ring
        const coords = shape.coordinates[0]?.map(([lat, lon]: [number, number]) => `${lon},${lat},0`).join(' ');
        kml += `<Placemark><name>${esc(shape.name || 'Polygon')}</name><Polygon><outerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></outerBoundaryIs></Polygon></Placemark>\n`;
      }
    }
    kml += `</Document></kml>`;
    // Download
    const blob = new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (selectedLayer?.layerName || 'attribute-table') + '.kml';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  const handleLayerChange = (newLayerId: number) => {
    const newLayer = layers.find((l) => l.layerId === newLayerId);

    logEvent({
      category: 'Attribute Table',
      action: 'LayerChanged',
      label: newLayer?.layerName || 'Unnamed Layer',
      old_layer_id: selectedLayerId,
      new_layer_id: newLayerId,
      old_layer_shape_count: shapes.length,
      new_layer_shape_count: newLayer?.shapes?.length || 0,
      old_layer_shape_types: Array.from(new Set(shapes.map((s: any) => s.type))).join(','),
      new_layer_shape_types: Array.from(new Set(newLayer?.shapes?.map((s: any) => s.type) || [])).join(','),
    });

    onLayerChange(newLayerId);
  };

  const handleClose = () => {
    logEvent({
      category: 'Attribute Table',
      action: 'AttributeTableClosed',
      label: selectedLayer?.layerName || 'Unnamed Layer',
      layer_id: selectedLayerId,
      session_duration_ms: Date.now() - (window as any).attributeTableOpenTime || 0,
      selected_shapes: selectedIds.length,
      sort_model: sortModel.length > 0 ? sortModel.map((s) => `${s.field}:${s.sort}`).join(',') : 'none',
    });

    onClose();
  };

  // Track drawer opening time
  useEffect(() => {
    if (open) {
      (window as any).attributeTableOpenTime = Date.now();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !editable) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      const isUndo = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey;
      const isRedo =
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && e.shiftKey);

      if (isUndo && onUndo && canUndo) {
        e.preventDefault();
        onUndo();
      } else if (isRedo && onRedo && canRedo) {
        e.preventDefault();
        onRedo();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, editable, onUndo, onRedo, canUndo, canRedo]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      variant="persistent"
      PaperProps={{
        sx: (theme) => ({
          width: 'calc(90vw - 65px)',
          p: 3,
          pr: 7, // Add right padding to prevent overlap with drawer buttons
          height: 'calc(100vh - 80px)', // Adjust height to account for logo
          top: 80, // Position below logo
          right: 65,
          overflowY: 'auto',
          ...frostedDrawerPaperSx(theme),
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          overflow: 'hidden',
          zIndex: 2201,
        }),
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        sx={(theme) => ({
          px: 2,
          py: 1,
          borderRadius: 2,
          ...frostedDrawerHeaderSx(theme, 0.22),
        })}
      >
        <Typography variant="h6" fontWeight={700}>
          {t('attributeTable.title')}
        </Typography>
        <Box>
          {editable && (
            <>
              <IconButton onClick={onUndo} disabled={!onUndo || !canUndo} title={t('attributeTable.undo')}>
                <Icon size={20} d={ICON_FALLBACKS.UndoOutlined} />
              </IconButton>
              <IconButton
                onClick={onRedo}
                disabled={!onRedo || !canRedo}
                title={t('attributeTable.redo')}
              >
                <Icon size={20} d={ICON_FALLBACKS.RedoOutlined} />
              </IconButton>
            </>
          )}
          <Badge color="primary" badgeContent={selectedIds.length > 0 ? selectedIds.length : undefined} showZero={false} sx={{ mr: 1 }}>
            <IconButton onClick={handleDownloadCSV} title={selectedIds.length > 0 ? t('attributeTable.downloadCsvSelected', { count: selectedIds.length }) : t('attributeTable.downloadCsv')}>
              <Icon size={20} name="file-arrow-down" />
            </IconButton>
          </Badge>
          <IconButton onClick={handleDownloadKML} sx={{ mr: 1 }} title={selectedIds.length > 0 ? t('attributeTable.downloadKmlSelected', { count: selectedIds.length }) : t('attributeTable.downloadKml')}>
            <Icon size={20} name="map" />
          </IconButton>
          <IconButton onClick={handleClose}>
            <Icon size={20} name="xmark" />
          </IconButton>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="layer-select-label">{t('attributeTable.layer')}</InputLabel>
        <Select
          labelId="layer-select-label"
          value={selectedLayerId}
          label={t('attributeTable.layer')}
          MenuProps={{
            sx: { zIndex: 2305 },
            PaperProps: {
              sx: { zIndex: 2306 },
            },
          }}
          onChange={(e) => handleLayerChange(Number(e.target.value))}
        >
          {layers.map((layer) => (
            <MenuItem key={layer.layerId} value={layer.layerId}>
              {layer.layerName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {!shapes || shapes.length === 0 ? (
        <Typography variant="body1">{t('attributeTable.noShapes')}</Typography>
      ) : (
        <Box sx={{ height: '70vh', width: '100%' }}>
          <DataGrid
            rows={tableRows}
            columns={columns}
            editMode="cell"
            processRowUpdate={handleProcessRowUpdate}
            onProcessRowUpdateError={() => {
              // Cell-level validation errors are rendered in edit cells.
            }}
            pageSizeOptions={[20, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 20, page: 0 } },
            }}
            disableRowSelectionOnClick
            autoHeight={false}
            checkboxSelection
            sortModel={sortModel}
            onSortModelChange={(model) => {
              logEvent({
                category: 'Attribute Table',
                action: 'SortChanged',
                label: selectedLayer?.layerName || 'Unnamed Layer',
                layer_id: selectedLayerId,
                old_sort: sortModel.length > 0 ? sortModel.map((s) => `${s.field}:${s.sort}`).join(',') : 'none',
                new_sort: model.length > 0 ? model.map((s) => `${s.field}:${s.sort}`).join(',') : 'none',
              });
              setSortModel(model);
            }}
            onRowSelectionModelChange={(rowSelectionModel) => {
              let ids: (string | number)[];
              if (Array.isArray(rowSelectionModel)) {
                ids = rowSelectionModel;
              } else if (rowSelectionModel && typeof rowSelectionModel === 'object' && 'ids' in rowSelectionModel) {
                ids = Array.from((rowSelectionModel as any).ids);
              } else {
                ids = [];
              }

              // Track row selection changes
              if (ids.length !== selectedIds.length) {
                logEvent({
                  category: 'Attribute Table',
                  action: 'RowSelectionChanged',
                  label: selectedLayer?.layerName || 'Unnamed Layer',
                  layer_id: selectedLayerId,
                  old_selection_count: selectedIds.length,
                  new_selection_count: ids.length,
                  selection_change: ids.length - selectedIds.length,
                });
              }

              setSelectedIds(ids);
              if (onSelectShapes) {
                onSelectShapes(ids);
              }
            }}
          />
        </Box>
      )}
    </Drawer>
  );
};

export default AttributeTableDrawer;
